/**
 * Consent gating (Atlas ch.7).
 *
 * Runs before any auction, in parallel with the config fetch — never chained
 * behind it, because chaining adds the CMP wait to the config wait and doubles
 * the delay before the first bid request (D7).
 *
 * The whole design turns on one distinction that is easy to get wrong:
 * **the timeout is for the CMP script to load, not for the visitor to decide.**
 * Most visitors never touch the banner. Waiting on a human either blocks ads
 * indefinitely or runs the auction with no consent at all. A CMP that is present
 * answers almost immediately — including "banner shown, no action yet", which is
 * a complete answer meaning *no consent exists*, and we act on it at once.
 *
 * So the deadline only ever expires when the CMP itself failed to appear.
 */

/** @typedef {"granted"|"denied"|"unknown"|"not_required"} ConsentState */

export const DEFAULT_CONSENT_TIMEOUT_MS = 1500;

/**
 * @typedef {object} ConsentResult
 * @property {ConsentState} state
 * @property {boolean} npa            Send `npa=1` to GAM — true unless consent is affirmative.
 * @property {boolean} runAuction     Always true. Prebid suppresses per vendor; we never skip wholesale.
 * @property {string=} tcString       Passed to bidders. Never stored, never sent to Playbid.
 * @property {boolean} gpc            The browser-level opt-out, read independently of any CMP.
 * @property {boolean} timedOut       The CMP never answered — a revenue signal, not just a compliance one.
 * @property {number} elapsedMs
 */

/**
 * Read GPC.
 *
 * Independent of the CMP and honoured even when a CMP reports consent: it is a
 * separate legal instrument in several US states, not an input the CMP gets to
 * override (D6).
 */
export function readGpc(nav = typeof navigator !== "undefined" ? navigator : undefined) {
  return nav?.globalPrivacyControl === true;
}

/**
 * Resolve consent, or give up at the deadline.
 *
 * @param {object} opts
 * @param {"required"|"not_required"} [opts.mode]  Per-publisher. Default `required` — fail closed.
 * @param {number} [opts.timeoutMs]
 * @param {object} [opts.win]        Injected for tests; defaults to `window`.
 * @param {() => number} [opts.now]
 * @returns {Promise<ConsentResult>}
 */
export function resolveConsent(opts = {}) {
  const {
    mode = "required",
    timeoutMs = DEFAULT_CONSENT_TIMEOUT_MS,
    win = typeof window !== "undefined" ? window : undefined,
    now = () => Date.now(),
  } = opts;

  const started = now();
  const gpc = readGpc(win?.navigator);

  const finish = (state, extra = {}) => ({
    state,
    // Affirmative consent is the ONLY case that gets personalised ads. GPC
    // overrides an affirmative CMP answer, per D6.
    npa: !(state === "granted" || state === "not_required") || gpc,
    runAuction: true,
    gpc,
    timedOut: false,
    elapsedMs: now() - started,
    ...extra,
  });

  // A publisher with no EU obligation opts out of the wait entirely (D5). The
  // default is the other way round: when in doubt, take the restrictive path.
  if (mode === "not_required") return Promise.resolve(finish(gpc ? "denied" : "not_required"));

  const api = typeof win?.__tcfapi === "function" ? win.__tcfapi : null;
  if (!api) {
    // No CMP present at all. Under `required` that resolves immediately as no
    // consent — there is nothing to wait for. Waiting out the full timeout here
    // would cost every visitor 1.5 s for an answer we already have.
    return Promise.resolve(finish("unknown"));
  }

  return new Promise((resolve) => {
    let settled = false;
    const done = (r) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(r);
    };

    const timer = setTimeout(
      // Past the ceiling we proceed on the most restrictive reading of what we
      // know (D1). A slow CMP is also a revenue leak only we can see, so the
      // timeout is reported, not just handled.
      () => done({ ...finish("unknown"), timedOut: true }),
      timeoutMs,
    );

    try {
      api("addEventListener", 2, (tcData, success) => {
        if (!success || !tcData) return done(finish("unknown"));

        if (tcData.gdprApplies === false) return done(finish("not_required"));

        // `cmpuishown` is RESOLUTION, not waiting: the banner is up and the
        // visitor has not acted, which is a complete answer — no consent exists
        // yet. Treating it as "keep waiting" is the mistake that makes this
        // whole subsystem block on a human (D2).
        const status = tcData.eventStatus;
        if (status === "cmpuishown") return done(finish("denied"));

        if (status === "tcloaded" || status === "useractioncomplete") {
          const granted = hasCoreConsent(tcData);
          return done(finish(granted ? "granted" : "denied", { tcString: tcData.tcString }));
        }
        // Any other status is not yet an answer; the deadline still applies.
      });
    } catch {
      // A CMP that throws is a CMP that did not answer. Never let it take the
      // page down with it.
      done(finish("unknown"));
    }
  });
}

/**
 * Is there enough consent to call this affirmative?
 *
 * Purpose 1 is "store and access information on a device" — without it nothing
 * downstream has a legal basis, so it is the single gate we read. Per-vendor
 * enforcement is Prebid's GDPR module's job, and reimplementing TCF vendor logic
 * ourselves would be worse than the module that already exists (D3).
 */
export function hasCoreConsent(tcData) {
  const p = tcData?.purpose?.consents;
  return Boolean(p && p[1] === true);
}
